// authService.ts

import type { LoginCredentials, LoginResponse } from "@/types/Login.types";
import api from "@/services/api";
import { type AxiosResponse } from "axios";
import { signOut } from "firebase/auth";
import { auth } from "@/firebase";

class AuthService {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response: AxiosResponse<LoginResponse> = await api.post("/auth/login", credentials);

      if (response.data.accessToken) {
        localStorage.setItem("accessToken", response.data.accessToken);
        localStorage.setItem("refreshToken", response.data.refreshToken);
      }

      return response.data;
    } catch (error: unknown) {
      console.error("Login error:", error);
      throw new Error("Login failed. Please check your credentials.");
    }
  }

  /**
   * Complete Logout: Clears Firebase, localStorage, and notifies backend API
   */
  async logout(): Promise<void> {
    try {
      // 1. Sign out from Firebase
      await signOut(auth);
    } catch (error) {
      console.error("Firebase sign-out failed:", error);
    } finally {
      // 2. Clear tokens from localStorage
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");

      // 3. Notify backend API
      api.post("/auth/logout").catch(() => {});
    }
  }

  async getCurrentUser() {
    try {
      const response = await api.get("/auth/me");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch current user:", error);
      throw error;
    }
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem("accessToken");
  }

  async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      // REMOVED: await signOut(auth); <--- THIS WAS BREAKING REFRESH
      const response = await api.post("/auth/refresh", { refreshToken });
      const newAccessToken = response.data.accessToken;

      localStorage.setItem("accessToken", newAccessToken);
      return newAccessToken;
    } catch {
      await this.logout(); // Clear tokens & session if refresh fails
      throw new Error("Session expired. Please login again.");
    }
  }
}

const authService = new AuthService();
export default authService;
